import React, { useCallback, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Typography, Box } from '@mui/material'
import { StyledDropzone } from './styles'
import { ReactComponent as AttachIcon } from '../../assets/paperclip.svg'
import { useTheme } from '@mui/material/styles'
import { ReactComponent as FileIcon } from '../../assets/file.svg'
import { grey, red } from '../../colors'
import { ReactComponent as InfoOutlinedIcon } from '../../assets/alert-circle.svg'
import DefaultModalActions from '../GenericModal/DefaultModalActions'
import CSVFileValidator from 'csv-file-validator'
import Papa from 'papaparse'
import UploadModalAlert from './UploadModalAlert'
import { bytesToKb } from '../../helpers/bytesToKb'
import { notification } from '../Notifications'
import GenericButton from '../GenericButton'
import CloseIcon from '@mui/icons-material/Close'
import api from '../../utils/api'
import { type ITaskPoll } from '../../models/Task'
import { taskDownload } from '../../features/TaskFeature'

interface DragAndDropProps {
  onDrop: (files: File) => void
  onConfirm?: () => Promise<void>
  onClose?: () => void
  loading?: boolean
  validationConfig?: any
  alertType?: null | string
  validationErrorBE?: any
  taskId?: string
}

const uploadedNodownloadErrorProps = {
  type: 'error',
  title: 'File Upload Received',
  message: 'We ran into an system error while processing your file. Some of your data may have been saved, but the upload didn’t complete. Please re-upload your file to finish the process. If this keeps happening, email us at support@classranked.com and we’ll help troubleshoot right away'
}

const uploadedSuccessProps = {
  type: 'processed',
  title: 'File Upload Received',
  message: 'Your CSV has been processed! You can now close this modal.'
}

const uploadedErrorProps = {
  type: 'error',
  title: 'File Upload Received',
  message: 'Your CSV file was uploaded with errors. Please download the CSV report to review the errors.'
}

const uploadedWarningProps = {
  type: 'warning',
  title: 'File Upload Received',
  message: 'Your CSV file was uploaded with warnings. Please download the CSV report to review the warnings.'
}

const successAlertProps = {
  type: 'success',
  title: 'File Upload Received',
  message: 'Your CSV file is being processed. You can close this modal at any time.'
}

const BeValidationAlertProps = {
  type: 'warning',
  title: 'Partial file upload',
  message: 'We encountered issues while uploading your file. Some rows in your file were not saved. Please download the Error Summary to review those rows and re-submit.'
}

const validationAlertProps = {
  type: 'error',
  title: 'Error: There are errors in your file.',
  message: 'Please download the Error Summary to correct the row with errors and try again.'
}

export const DragAndDrop: React.FC<DragAndDropProps> = ({ onDrop, onConfirm, onClose, loading, validationConfig, alertType, validationErrorBE, taskId }) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [validationError, setValidationError] = useState<any>(null)

  const [status, setStatus] = useState<ITaskPoll>({ status: 'PENDING' })
  const [taskLoading, setTaskLoading] = useState(false)
  const [taskError, setTaskError] = useState(false)

  useEffect(() => {
    let isMounted = true
    if (alertType !== 'success') {
      return
    }

    setTaskLoading(true)

    // Function to start polling
    const startPolling = (): void => {
      api.get<ITaskPoll>(`/api/tasks/longpoll/${taskId}/`)
        .then(response => {
          if (isMounted) {
            if (status.status !== 'PENDING') {
              setTaskLoading(false)
              return
            }
            if (response?.data?.status !== 'PENDING') {
              setStatus(response.data)
              setTaskLoading(false)
              return
            }
            startPolling() // Continue polling after receiving data
          }
        })
        .catch(error => {
          console.error('Error while polling:', error)
          if (error.status === 500) {
            setTaskLoading(false)
            setTaskError(true)
            return
          }
          if (isMounted) {
            setTimeout(startPolling, 2000) // Retry after a delay on error
          }
        })
    }

    // Start the long polling when the component mounts
    startPolling()

    // Cleanup on component unmount
    return () => {
      isMounted = false
    }
  }, [alertType])

  const theme = useTheme()

  const handleDrop = useCallback((acceptedFiles: File[]) => {
    setSelectedFiles(acceptedFiles)
    onDrop(acceptedFiles[0])

    if (validationConfig) {
      CSVFileValidator(acceptedFiles[0], validationConfig)
        .then((csvData) => {
          const invalidData = csvData.inValidData
          if (invalidData?.length > 0) {
            setValidationError(invalidData)
          }
        })
        .catch((err) => {
          console.error(err)
        })
    }
  }, [selectedFiles, onDrop])

  const {
    getRootProps,
    fileRejections,
    getInputProps,
    isFocused,
    isDragActive,
    isDragAccept,
    isDragReject
  } = useDropzone({
    onDrop: handleDrop,
    noClick: selectedFiles.length > 0,
    maxFiles: 1,
    multiple: false,
    accept: { 'text/csv': ['.csv'] }
  })

  const downloadFile = (data: string, fileName: string): void => {
    console.log('data', data)
    const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const downloadErrorsAsCSV = (): void => {
    const validationErrors = validationErrorBE || validationError

    if (!validationErrors || !Array.isArray(validationErrors)) {
      notification('No validation errors found or incorrect format', { variant: 'error' })
      return
    }

    const csvData = Papa.unparse(validationErrors)
    const fileName = 'validation-error.csv'
    downloadFile(csvData, fileName)
    notification('Successfully downloaded', { variant: 'success', subTitle: fileName ?? '' })
  }

  const isFileFormateError =
    fileRejections && fileRejections[0]?.errors[0].code === 'file-invalid-type'
  const isFileDrop = selectedFiles.length > 0

  const confirmButtonProps = (): Record<string, any> => {
    switch (true) {
      case isFileFormateError || taskError:
        return {
          text: 'Close',
          onClick: onClose
        }
      case alertType === 'success':
        return {
          text: 'Download',
          loading: taskLoading,
          onClick: () => {
            if (taskId) {
              void taskDownload(taskId)
            }
          }
        }
      case Boolean(validationError):
      case alertType === 'warning':
        return {
          text: 'Download Error Summary',
          onClick: downloadErrorsAsCSV
        }
      default:
        return {
          text: 'Upload',
          onClick: onConfirm,
          disabled: !isFileDrop,
          loading
        }
    }
  }

  const styledDropZoneProps = {
    isFocused,
    isDragAccept,
    isDragReject,
    isDragActive,
    isFileDrop,
    isError: isFileFormateError
  }

  const getTitle = (): string => {
    switch (true) {
      case isFileFormateError:
        return 'Processing your file, please wait...'
      case alertType === 'warning':
      case alertType === 'success':
        return 'File upload complete, please review'
      case Boolean(validationError):
        return 'File processing complete, please review'
      case isFileDrop:
        return 'File processing complete'
      default:
        return 'Choose a file to upload'
    }
  }

  const showAlert = (): JSX.Element | undefined => {
    switch (true) {
      case taskError:
        return <UploadModalAlert {...uploadedNodownloadErrorProps} taskId={taskId} />
      case status?.status === 'SUCCESSFUL':
        return <UploadModalAlert {...uploadedSuccessProps} taskId={taskId} />
      case status?.status === 'WARNING':
        return <UploadModalAlert {...uploadedWarningProps} taskId={taskId}/>
      case status?.status === 'ERROR':
        return <UploadModalAlert {...uploadedErrorProps} taskId={taskId}/>

      case alertType === 'success':
        return <UploadModalAlert {...successAlertProps} taskId={taskId}/>
      case alertType === 'warning':
        return <UploadModalAlert {...BeValidationAlertProps} onDownloadErrors={downloadErrorsAsCSV}/>
      case validationError !== null:
        return <UploadModalAlert {...validationAlertProps} onDownloadErrors={downloadErrorsAsCSV}/>
      default:
        break
    }
  }

  useEffect(() => {
    if (loading) {
      const handleBeforeUnload = (event: BeforeUnloadEvent): string => {
        event.preventDefault()
        const confirmationMessage = 'Changes you made may not be saved.'
        return confirmationMessage
      }
      window.addEventListener('beforeunload', handleBeforeUnload)
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload)
      }
    }
  }, [loading])

  const handleRemoveFile = (): void => {
    setSelectedFiles([])
    setValidationError(null)
  }

  return (
    <Box>
      <Typography fontSize={'14px'} mb={5}>
        {getTitle()}
      </Typography>
      {showAlert()}
      {
        alertType !== 'success' &&
        <StyledDropzone {...getRootProps()} {...styledDropZoneProps}>
          <input {...getInputProps()} />

          {isFileDrop && (
            <Box display='flex' justifyContent='space-between' width='100%' alignItems='center' >
              <Box display="flex" gap={theme.spacing(3)} width="80%" alignItems="center">
                <FileIcon />
                <Box display="flex" flexDirection="column">
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: grey[950] }}>
                    {selectedFiles[0]?.name}
                  </Typography>
                  <Typography sx={{ fontSize: '14px', fontWeight: 400, color: grey[850] }}>
                    {bytesToKb(selectedFiles?.[0]?.size)}
                  </Typography>
                </Box>
              </Box>
            {!alertType && <GenericButton disabled={loading} icon={<CloseIcon sx={{ color: grey[400] }} /> } onClick={handleRemoveFile} />}
            </Box>
          )}

          {isFileFormateError && (
            <Box display="flex" gap={theme.spacing(3)} width="80%" alignItems="center">
              <InfoOutlinedIcon width={theme.spacing(6)} height={theme.spacing(6)} />
              <Box display="flex" flexDirection="column">
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: red[700] }}>
                  Error: File must be in CSV format.
                </Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 400, color: red[600] }}>
                  Please try again.
                </Typography>
              </Box>
            </Box>
          )}

          {!isFileDrop && !isFileFormateError && (
            <Box display="flex" justifyContent="center" alignItems="center" gap={theme.spacing(2)}>
              <AttachIcon />
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: grey[950] }}>
                Upload a CSV file
              </Typography>
            </Box>
          )}
        </StyledDropzone>
      }

      <DefaultModalActions
        confirmButtonProps={confirmButtonProps()}
        destructiveButtonProps={{
          onClick: onClose,
          hideButton: isFileFormateError || taskError,
          disabled: loading,
          text: (validationError || alertType === 'warning' || alertType === 'success') ? 'Close' : 'Cancel'
        }}
      />
    </Box>
  )
}

export default DragAndDrop
